package com.evoapps.evoshift.domain.usecase.financial

import com.evoapps.evoshift.domain.model.workerId
import com.evoapps.evoshift.domain.model.isPaidFor
import com.evoapps.evoshift.domain.market.payroll.PayrollProfile
import com.evoapps.evoshift.domain.market.payroll.PayrollRules
import com.evoapps.evoshift.domain.market.payroll.PayslipInput
import com.evoapps.evoshift.domain.market.payroll.MonthPayFactors
import com.evoapps.evoshift.domain.market.payroll.WorkedSpan
import com.evoapps.evoshift.domain.model.AdjustmentType
import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.model.paidSpan
import com.evoapps.evoshift.domain.model.workedHours
import com.evoapps.evoshift.domain.model.plannedSpan
import com.evoapps.evoshift.domain.model.CompensationModel
import com.evoapps.evoshift.domain.model.EmployeePayroll
import com.evoapps.evoshift.domain.model.RosterPayroll
import com.evoapps.evoshift.domain.model.RosterStatus
import com.evoapps.evoshift.domain.model.SystemContractType
import com.evoapps.evoshift.domain.repository.AbsenceRepository
import com.evoapps.evoshift.domain.repository.FinancialRepository
import com.evoapps.evoshift.domain.repository.RosterRepository
import com.evoapps.evoshift.domain.repository.ShiftRepository
import com.evoapps.evoshift.domain.repository.UserRepository
import com.evoapps.evoshift.domain.repository.WorkplaceRepository
import com.evoapps.evoshift.domain.usecase.CalculateShiftCostUseCase
import com.evoapps.evoshift.domain.usecase.finance.MonthWork
import com.evoapps.evoshift.domain.util.calculateDurationHours

/**
 * The month's payroll of a roster (financial managers): for everyone who works it, or is paid a
 * salary there, the gross from the shift cost snapshots (clocked pay once clocked) or the salary
 * plus night allowances, bonuses on top, and the payslip of the workplace market's rules.
 * Deductions come off the take-home pay; expense refunds are paid back untaxed. Each person also
 * carries their adjustments one by one (SH-78) and their shifts (what an adjustment may refer to).
 * SH-57: overtime against the settlement period's norms, Sunday and holiday work and the
 * minimum-wage top-up go on top of the gross (PayrollRules.monthFactors); so do sick pay, the ZUS
 * sickness benefit and holiday pay from the approved absences, a salary changed during the month
 * pro rata, and PPK in the payslip. Shifts on days of leave that were not clocked are not paid.
 */
class GeneratePayrollReportUseCase(
    private val shiftRepository: ShiftRepository,
    private val financialRepository: FinancialRepository,
    private val userRepository: UserRepository,
    private val workplaceRepository: WorkplaceRepository,
    private val rosterRepository: RosterRepository,
    private val calculateShiftCost: CalculateShiftCostUseCase,
    /** SH-57: approved sick leave and annual leave; without it the estimate has no leave pay. */
    private val absenceRepository: AbsenceRepository? = null,
    /**
     * SH-147: the daily records (and the month's totals the server adds up from them) recomputed
     * when shifts were priced here, so the Finances screen counts the same shifts as this report.
     */
    private val regenerateDailyRecords: com.evoapps.evoshift.domain.usecase.finance.RegenerateDailyRecordsUseCase? = null,
    /** SH-206: who does not count in the labour figures is never flagged as missing pay. */
    private val labourInclusion: com.evoapps.evoshift.domain.budget.LabourInclusionReader? = null,
    /** SH-223: approved overtime and work events (paid work outside the shifts); without it none. */
    private val workRequestRepository: com.evoapps.evoshift.domain.repository.WorkRequestRepository? = null
) {
    suspend operator fun invoke(rosterId: String): Result<RosterPayroll> = runCatching {
        // Price what is missing or stale first (new assignees, clocked shifts); history stays.
        val priced = calculateShiftCost.refreshRoster(rosterId).getOrDefault(0)
        val roster = rosterRepository.getRoster(rosterId).getOrThrow()
        if (priced > 0 && roster.status != RosterStatus.LOCKED) {
            regenerateDailyRecords?.invoke(roster.workplaceId, rosterId, roster.year, roster.month)
        }
        val workplace = workplaceRepository.getWorkplace(roster.workplaceId).getOrThrow()
        val settings = workplace.settings
        val rules = PayrollRules.of(settings.market)

        // An unpaid owner is not on the payroll at all; SH-232: nor a shift on a day someone works without pay.
        val shifts = shiftRepository.getShiftsForRoster(rosterId).getOrThrow()
            .filter { shift -> shift.workerId?.let { workplace.isPaidFor(it, shift) } == true }
        val snapshots = financialRepository.getShiftFinancialsForRoster(rosterId).getOrThrow()
            .associateBy { it.shiftId }
        // Everyone's bonuses, deductions and refunds of the month in one read (SH-78).
        val allAdjustments = financialRepository.getAdjustmentsForRoster(rosterId).getOrDefault(emptyList())
            .groupBy { it.userId }
        val team = userRepository.getAssignableEmployees(roster.workplaceId).getOrDefault(emptyList())
        val names = team.associate { it.uid to it.displayName }
        // SH-57: the earlier months of the settlement period, for the overtime against its norm.
        val earlierShifts = settings.payroll.settlementMonths(roster.month).filter { it < roster.month }.flatMap { month ->
            shiftRepository.getShiftsForRoster(RosterIds.of(roster.workplaceId, roster.year, month)).getOrDefault(emptyList())
        }.filter { shift -> shift.workerId?.let { workplace.isPaidFor(it, shift) } == true }
        val engagements = (team.map { it.uid }.filter { workplace.isPaidIn(it, com.evoapps.evoshift.domain.budget.LabourInclusion.monthOf(roster.year, roster.month)) } + shifts.map { it.workerId!! }).distinct().associateWith { uid ->
            userRepository.getSensitiveUserData(uid).getOrNull()?.financialEngagements?.get(roster.workplaceId)
        }
        // SH-206: their pay stays theirs; only the missing-pay flag leaves out who does not count.
        val notCounted = labourInclusion?.people(
            workplace, engagements.keys, com.evoapps.evoshift.domain.budget.LabourInclusion.monthOf(roster.year, roster.month),
            engagements.filterValues { it != null }.mapValues { it.value!! }
        )?.getOrNull()?.excluded.orEmpty()
        // SH-57: the year's approved leave; the 12 months before only when someone is on leave this month.
        val absences = absenceRepository?.getAbsencesForWorkplace(roster.workplaceId)?.getOrNull().orEmpty()
        val leaveByUser = engagements.keys.associateWith { uid -> MonthWork.leaveDays(absences, uid, roster.workplaceId, roster.year, roster.month) }
        val workRequests = workRequestRepository?.forWorkplace(roster.workplaceId)?.getOrNull().orEmpty()
        val monthFirst = MonthWork.firstDay(roster.year, roster.month)
        val anyoneOnLeave = leaveByUser.values.any { days -> days.any { it.date >= monthFirst } }
        val earlierYear = if (!anyoneOnLeave) emptyList() else {
            MonthWork.earlierRosterIds(roster.workplaceId, roster.year, roster.month).flatMap { id ->
                shiftRepository.getShiftsForRoster(id).getOrDefault(emptyList())
            }.filter { shift -> shift.workerId?.let { workplace.isPaidFor(it, shift) } == true }
        }

        val employees = engagements.mapNotNull { (uid, engagement) ->
            val theirs = shifts.filter { it.workerId == uid }
            val leave = leaveByUser[uid].orEmpty()
            val leaveThisMonth = leave.filter { it.date >= monthFirst }
            // SH-57: a shift on a day of leave that nobody clocked was not worked.
            val worked = theirs.filterNot { MonthWork.isOnLeave(it, leaveThisMonth) }
            // The terms in force at the end of the month (a change during it is paid pro rata in the factors).
            val salary = engagement?.compensationOn(MonthWork.lastDay(roster.year, roster.month).toString()) as? CompensationModel.MonthlyFixed
            val adjustments = allAdjustments[uid].orEmpty().sortedWith(compareBy({ it.date }, { it.metadata.createdAt }))
            // Listed when they work, earn a salary, are on leave or got a bonus, deduction or refund this month.
            if (theirs.isEmpty() && salary == null && adjustments.isEmpty() && leaveThisMonth.isEmpty()) return@mapNotNull null
            val pricedShifts = worked.mapNotNull { shift -> snapshots[shift.id]?.takeIf { it.userId == null || it.userId == uid }?.let { shift.date to it.costs } }
            val costs = pricedShifts.map { it.second }
            // SH-149: the month's pay by day, for the payroll facts in force on each (a status that started or ended).
            val shares = engagement?.let { e ->
                MonthWork.profileShares(e, roster.year, roster.month, pricedShifts.groupBy({ it.first }, { it.second.actualPayout }).mapValues { it.value.sum() })
            }.orEmpty()
            val plannedShares = engagement?.let { e ->
                MonthWork.profileShares(e, roster.year, roster.month, pricedShifts.groupBy({ it.first }, { it.second.estimatedPayout }).mapValues { it.value.sum() })
            }.orEmpty()
            val monthEndProfile = engagement?.profileOn(MonthWork.lastDay(roster.year, roster.month).toString()) ?: PayrollProfile()
            // SH-212: the contract in force at the end of the month, as the pay terms.
            val monthEndContract = engagement?.contractOn(MonthWork.lastDay(roster.year, roster.month).toString())?.contract ?: SystemContractType.NOT_SET
            // SH-147: the shifts' employer cost alone, as the daily records count it (planned; payable).
            val shiftsPlanned = theirs.mapNotNull { shift -> snapshots[shift.id]?.takeIf { it.userId == null || it.userId == uid }?.costs?.employerCost }.sum()
            val shiftsPayable = worked.mapNotNull { shift ->
                snapshots[shift.id]?.takeIf { it.userId == null || it.userId == uid }?.costs?.let { it.actualEmployerCost ?: it.employerCost }
            }.sum()
            val pay = salary?.let { it.baseSalary + costs.sumOf { c -> c.nightAllowance } } ?: costs.sumOf { it.actualPayout }
            // SH-46: the same for the rostered hours, before any clocking.
            val plannedPay = salary?.let { it.baseSalary + costs.sumOf { c -> c.nightAllowance } } ?: costs.sumOf { it.estimatedPayout }

            fun sum(type: AdjustmentType) = adjustments.filter { it.type == type }.sumOf { it.amount }
            val bonuses = sum(AdjustmentType.BONUS)

            // SH-57: overtime, Sunday and holiday work and the minimum wage, for the paid and the planned hours.
            val periodShifts = earlierShifts.filter { it.workerId == uid } + worked
            val earlierWork = if (leaveThisMonth.isEmpty()) emptyList()
            else earlierYear.filter { it.workerId == uid }.mapNotNull { it.paidSpan(settings.breakPolicy, settings.clockReview) }
            // SH-223: approved overtime and work events outside the shifts.
            val extraWork = MonthWork.extraWork(
                workRequests, uid, roster.workplaceId, roster.year, roster.month,
                workedShiftDates = worked.map { it.date }.toSet(), isWorkingDay = { rules.isLeaveDay(it) }
            )
            fun factors(spans: List<WorkedSpan>): MonthPayFactors? = engagement?.let {
                rules.monthFactors(MonthWork.input(roster.year, roster.month, it, spans, settings.payroll, bonuses, leave, earlierWork, extraWork))
            }
            val factors = factors(periodShifts.mapNotNull { it.paidSpan(settings.breakPolicy, settings.clockReview) })
            val plannedFactors = factors(periodShifts.mapNotNull { it.plannedSpan() })

            EmployeePayroll(
                userId = uid,
                employeeName = names[uid] ?: userRepository.getUserProfile(uid).getOrNull()?.displayName ?: uid,
                contract = monthEndContract,
                plannedHours = theirs.sumOf { calculateDurationHours(it.plannedTime.start, it.plannedTime.end) },
                // SH-116: worked hours leave the unpaid break out.
                workedHours = theirs.sumOf { shift -> shift.workedHours(settings.breakPolicy, settings.clockReview) ?: 0.0 },
                paidHours = theirs.sumOf { shift ->
                    shift.workedHours(settings.breakPolicy, settings.clockReview) ?: calculateDurationHours(shift.plannedTime.start, shift.plannedTime.end)
                },
                shiftsWithoutCost = worked.size - costs.size,
                bonuses = bonuses,
                deductions = sum(AdjustmentType.DEDUCTION),
                expenseRefunds = sum(AdjustmentType.EXPENSE_REFUND),
                adjustments = adjustments,
                shifts = theirs.sortedBy { it.plannedTime.start },
                payslip = rules.payslip(
                    PayslipInput(
                        year = roster.year,
                        month = roster.month,
                        contract = monthEndContract,
                        profile = monthEndProfile,
                        gross = pay + bonuses + (factors?.extraGross ?: 0.0),
                        settings = settings.payroll,
                        contributionFreeGross = factors?.contributionFreeGross ?: 0.0,
                        paidByOthers = factors?.paidByZus ?: 0.0
                    ),
                    shares
                ),
                plannedPayslip = rules.payslip(
                    PayslipInput(
                        year = roster.year,
                        month = roster.month,
                        contract = monthEndContract,
                        profile = monthEndProfile,
                        gross = plannedPay + bonuses + (plannedFactors?.extraGross ?: 0.0),
                        settings = settings.payroll,
                        contributionFreeGross = plannedFactors?.contributionFreeGross ?: 0.0,
                        paidByOthers = plannedFactors?.paidByZus ?: 0.0
                    ),
                    plannedShares
                ),
                factors = factors,
                plannedFactors = plannedFactors,
                missingPay = engagement == null && theirs.any { !it.notWorked } && workplace.expectsPayTerms(uid) && uid !in notCounted,
                worksWithoutPay = engagement == null && uid == workplace.ownerId,
                shiftsEmployerCostPlanned = shiftsPlanned,
                shiftsEmployerCost = shiftsPayable
            )
        }.sortedBy { it.employeeName.lowercase() }

        RosterPayroll(
            rosterId, roster.year, roster.month, settings.market, settings.currency, employees,
            locked = roster.status == RosterStatus.LOCKED
        )
    }
}
